//PrintUI.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as ExportMap from "./ExportMap";
import * as GraLay from "../Util/GraLay";
import * as MainMap from "../MainMap";
import * as PrintMap from "./PrintMap";
import * as Search from "../Search/Search";
import * as Settings from "../Settings";
import * as SnackBar from "../Util/SnackBar";
import * as Util from "../Util/Util";
import * as Util_MUI from "../Util/Util_MUI";
import * as WidgetHeading from "../Widgets/WidgetHeading";

import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import {
   Button, Checkbox, FormControl, FormControlLabel, FormLabel, FormGroup, Icon, IconProps,
   Link, Paper, Radio, TextField, Typography
} from "@mui/material";

import React from "react";

const toolName: string = "Print";

export type exportFormatType2D = "pdf" | "png32" | "png8" | "jpg" | "gif" | "eps" | "svg" | "svgz";
export type exportFormatType2D_Screenshot = "png" | "jpg";
type exportFormatType3D = "png" | "jpg";

// To set default format, move it to index zero.
const exportFormats2D = ["JPG - JPEG Compressed Image (small)", "PDF - Adobe PDF (small - export only)", "PNG8 - 256 Color PNG Image (large)", "PNG32 - Full Color PNG Image (very large)", "SVG - Scalable Vector Graphics File", "GIF - 256 Color GIF Image (large)", "EPS - Encapsulated PostScript (export only)"];
const exportFormats2D_Screenshot = ["JPG - JPEG Compressed Image (small)", "PNG - PNG Image (large)"];
const exportFormats3D = ["JPG - JPEG Compressed Image (small)", "PNG - PNG Image (large)"];

export let mapTitle = "";

export let scaleText: string;
export let mapScaleFeetPerInch: number;
export let isStandardScale = false;

export let paperSize: string;
export let pageWidth = 8.5;
export let pageHeight = 11;

export let portrait = true;
export let landscape = false;
export let includeLegend = false;
export let includeDataTable = true;

export let exportImageFormat = "jpg";
let exportFormatLabel: string;

let drag_handle: __esri.Handle;
let stationary_handle: __esri.Handle;

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   Util.logMsg("Top of PrintUI.Render");

   //Initialize.
   if (typeof scaleText === "undefined") {
      scaleText = Settings.print.scales[0];
      paperSize = Settings.print.paperSizes[0];
   }

   if (MainMap.view2D) {
      if (Settings.useScreenShotForExport) {
         if (!exportFormats2D_Screenshot.includes(exportFormatLabel)) {
            exportFormatLabel = exportFormats2D_Screenshot[0];
         }

      } else {
         if (!exportFormats2D.includes(exportFormatLabel)) {
            exportFormatLabel = exportFormats2D[0];
         }
      }

   } else {
      if (!exportFormats3D.includes(exportFormatLabel)) {
         exportFormatLabel = exportFormats3D[0];
      }
   }

   setStandardScale();

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
      drag_handle = MainMap.activeView.on("drag", function () {
         if (DrawerMenu.curToolName === "Print") {
            PrintMap.setPrintParameters(false);
         }
      });

      stationary_handle = reactiveUtils.when(() => MainMap.activeView?.stationary === true, function () {
         if (DrawerMenu.curToolName === "Print") {
            if (isStandardScale) {
               PrintMap.setPrintParameters();
            } else {
               PrintMap.setPrintParameters(false);
            }
         }
      });

      let resizeTimeout: NodeJS.Timeout;

      window.addEventListener('resize', function () {
         if (DrawerMenu.curToolName === "Print") {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(function () {
               if (isStandardScale) {
                  PrintMap.setPrintParameters();
               } else {
                  PrintMap.setPrintParameters(false);
               }
            }, 800); // Delay ms after resizing ends.
         }
      }, { passive: true });

      PrintMap.setPrintParameters();

      //Component will unmount.
      return () => {
         drag_handle.remove();
         stationary_handle.remove();
      };
   }, []);

   //Component did update.
   React.useEffect(() => {
   });

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Map title.
   function titleChanged(event: React.ChangeEvent<HTMLInputElement>) {
      mapTitle = event.target.value;
      refresh();
   }

   function mapScaleChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      scaleText = event.target.value;
      setStandardScale();
      PrintMap.setPrintParameters();
      refresh();
   }

   function paperSizeChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      paperSize = event.target.value;

      //Full paper size (inches).
      const sa: string[] = paperSize.toLowerCase().split("x");
      pageWidth = Util.parseFlt(sa[1]);
      pageHeight = Util.parseFlt(sa[0]);

      if (portrait) {
         pageWidth = Util.parseFlt(sa[0]);
         pageHeight = Util.parseFlt(sa[1]);
      }

      PrintMap.setPrintParameters();
      refresh();
   }

   function orientationChanged(event: React.ChangeEvent<HTMLInputElement>): void {
      const checked: boolean = event.target.checked;
      const value: string = event.target.defaultValue;

      if (value === "Portrait") {
         portrait = checked;
         landscape = !checked;

      } else {
         landscape = checked;
         portrait = !checked;
      }

      let temp: number = pageWidth;
      pageWidth = pageHeight;
      pageHeight = temp;

      PrintMap.setPrintParameters();
      refresh();
   }

   function includeLegendOrDataTableChanged(event: React.ChangeEvent<HTMLInputElement>): void {
      const checked: boolean = event.target.checked;
      const value: string = event.target.defaultValue;

      if (value === "Legend") {
         includeLegend = checked;

      } else if (value === "Data Table") {
         includeDataTable = checked;
      }
      PrintMap.setPrintParameters();
      refresh();
   }

   function exportFormatChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      exportFormatLabel = event.target.value;
      const sa: string[] = exportFormatLabel.split("-");
      exportImageFormat = sa[0].trim().toLowerCase();
      refresh();
   }

   //Set predefined scale per inch from dropdown list.
   function setStandardScale() {
      isStandardScale = false;

      //CUSTOM! - Using current scale did not provide a good scale on landscape, getting rid of the option. (Rob)
      if (scaleText === Settings.print.scales[0]) {
         //get the current scale
         let crntscale = MainMap.activeView.scale / 12;
         //find the closest higher standard scale and set it
         const closestScale = findClosestHigherScale(
            crntscale,
            Settings.print.scales
         );

         if (closestScale !== undefined) {
            scaleText = closestScale;
         }
      }
      //END OF CUSTOM!

      if (scaleText !== Settings.print.scales[0]) {
         isStandardScale = true;
         const sa = scaleText.split("=");
         mapScaleFeetPerInch = Util.parseFlt(sa[1]);
         if (scaleText.Contains("ile")) {
            mapScaleFeetPerInch *= 5280;
         }
      }
   }

   //CUSTOM! Added to find the closest higher standard print scale to set the value instead of current. (Rob)
   function findClosestHigherScale(
      targetFeet: number,
      scales: string[]
   ): string | undefined {

      let closestScale: string | undefined;
      let smallestHigherDistance = Infinity;

      for (const scale of scales) {
         const match = scale.match(
            /1 inch\s*=\s*([\d.]+)\s*(feet|mile)/i
         );

         if (!match) continue;

         const value = Number(match[1]);
         const unit = match[2].toLowerCase();

         const distanceFeet =
            unit === "mile" ? value * 5280 : value;

         if (
            distanceFeet >= targetFeet &&
            distanceFeet < smallestHigherDistance
         ) {
            smallestHigherDistance = distanceFeet;
            closestScale = scale;
         }
      }

      // If no higher scale exists, return the largest available option.
      if (closestScale === undefined) {
         let largestDistance = -Infinity;

         for (const scale of scales) {
            const match = scale.match(
               /1 inch\s*=\s*([\d.]+)\s*(feet|mile)/i
            );

            if (!match) continue;

            const value = Number(match[1]);
            const distanceFeet =
               match[2].toLowerCase() === "mile"
                  ? value * 5280
                  : value;

            if (distanceFeet > largestDistance) {
               largestDistance = distanceFeet;
               closestScale = scale;
            }
         }
      }

      return closestScale;
   }
   //END OF CUSTOM!

   //Show map layers menu.
   function showMapLayers() {
      DrawerMenu.showTool("Map Layers");
   }

   //Print button click event.
   function handlePrint() {
      if (!PrintMap.busy) {

         if (!MainMap.activeView.stationary) {
            SnackBar.show("Wait until map is ready");
            return;
         }

         GraLay.temp.removeAll();
         //Print map step 1 of 4.
         setTimeout(function () { PrintMap.getMapURL(); }, 10); //Wait for yellow printing outline to be cleared.
      }
   }

   //Export map.
   function handleExport() {
      if (!PrintMap.busy) {
         if (MainMap.view2D) { // 2D.
            if (Settings.useScreenShotForExport) {

               if (!MainMap.activeView.stationary) {
                  SnackBar.show("Wait until map is ready");
                  return;
               }

               GraLay.temp.removeAll();
               setTimeout(function () { PrintMap.getMapURL(true); }, 10); //Wait for yellow printing outline to be cleared.

            } else { //Use ESRIs print module for the export.
               ExportMap.print(exportImageFormat as exportFormatType2D);
            }

         } else { // 3D screenshot.
            PrintMap.getMapURL(true);
         }
      }
   }
   //Download georeference world file.
   function handleGeoRef() {
      if (!PrintMap.busy) {
         if (MainMap.view2D) { // 2D only.
            if (Settings.useScreenShotForExport) {

               if (!MainMap.activeView.stationary) {
                  SnackBar.show("Wait until map is ready");
                  return;
               }
               PrintMap.getMapURL(false, true);
            }
         }
      }
   }

   let dataTableVisible: boolean = false;
   if (Search.getActiveLayerNumRec() > 0) {
      dataTableVisible = true;
   }

   let legendVisible: boolean = true;
   if (landscape && paperSize.StartsWith("8.5") && includeDataTable && dataTableVisible) {
      includeLegend = false;
      legendVisible = false;
   }

   let exportFormats = exportFormats3D;
   if (MainMap.view2D) {
      exportFormats = exportFormats2D;
      if (Settings.useScreenShotForExport) {
         exportFormats = exportFormats2D_Screenshot;
      }
   }

   let printIconColor: IconProps["color"] = "action";

   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   const styles: Default.iStyles = {
      formControlLabel: { margin: 0, padding: 0, paddingRight: 15 },
   };

   return (
      <Paper>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div id={scrollDivID} style={Default.styles.appToolDiv}>
            <TextField style={{ marginTop: 7 }}
               id="print-title"
               label="Title"
               value={mapTitle}
               margin="dense"
               autoFocus={false}
               fullWidth
               onChange={titleChanged}
            />

            <TextField select style={{ marginRight: 60, marginTop: 14 }}
               label="Map Scale"
               title="Map Scales"
               slotProps={{
                  htmlInput: { id: "map-scales" },
                  inputLabel: { htmlFor: "map-scales" }
               }}
               value={scaleText}
               onChange={mapScaleChanged}
            >
               {Util_MUI.buildSelectMenuItems(Settings.print.scales)}
            </TextField>

            <TextField select style={{ marginTop: 14 }}
               label="Paper Size"
               title="Paper Size"
               slotProps={{
                  htmlInput: { id: "paper-size" },
                  inputLabel: { htmlFor: "paper-size" }
               }}
               value={paperSize}
               onChange={paperSizeChanged}
            >
               {Util_MUI.buildSelectMenuItems(Settings.print.paperSizes)}
            </TextField>

            <FormControl style={{ marginTop: 14 }}>
               <FormLabel style={{ color: 'rgba(183, 28, 28, 0.87)' }} htmlFor="portrait-id-prt">Layout</FormLabel>

               <FormGroup row={true}>
                  <FormControlLabel style={styles.formControlLabel}
                     control={<Radio key={Util.uid()} inputProps={{ id: "portrait-id-prt" }} value="Portrait" checked={portrait} onChange={orientationChanged} />}
                     label="Portrait "
                  />
                  <FormControlLabel style={styles.formControlLabel}
                     control={<Radio key={Util.uid()} inputProps={{ id: "landscape-id-prt" }} value="Landscape" checked={landscape} onChange={orientationChanged} />}
                     label="Landscape"
                  />
               </FormGroup>
            </FormControl>

            {(legendVisible || dataTableVisible) &&
               <FormControl style={{ marginTop: 7 }}>

                  <FormLabel style={{ color: 'rgba(183, 28, 28, 0.87)' }} htmlFor="legend-id-prt">Include</FormLabel>

                  <FormGroup row={true}>
                     {dataTableVisible && <FormControlLabel style={styles.formControlLabel} title="Show database details"
                        control={<Checkbox value="Data Table" inputProps={{ id: "data-table-id-prt" }} checked={includeDataTable} color="primary" onChange={includeLegendOrDataTableChanged} />}
                        label="Data Table"
                     />}
                     {legendVisible &&
                        <FormControlLabel style={styles.formControlLabel}
                           control={<Checkbox value="Legend" inputProps={{ id: "legend-id-prt" }} checked={includeLegend} color="primary" onChange={includeLegendOrDataTableChanged} />}
                           label="Legend"
                        />}
                  </FormGroup>
               </FormControl>}

            <div className="app-flex-row-start" style={{ marginTop: 7 }}>
               <Button variant="outlined" color="info" onClick={handlePrint} title="Print Map" aria-label="Print Map">
                  <Icon color={printIconColor}>print</Icon>
                  <span style={{ marginLeft: 7 }}></span>
                  Print
               </Button>
            </div>

            <Typography variant="body2" style={{ margin: 7, marginTop: 15 }}>
               <b><i>Tip:</i></b>&nbsp;&nbsp;To save the printed map as a PDF, set the printer destination to &quot;<b>Save as PDF</b>&quot; using the Chrome or Edge browser.
            </Typography>

            {includeLegend && <Typography variant="body2" style={{ margin: 7, marginTop: 15 }}>
               <b><i>Tip:</i></b>&nbsp;&nbsp;To set the order of the items in the legend, use the
               <Link component="button" color="textSecondary" onClick={showMapLayers} title="Map Layers" aria-label="Map Layers">
                  &nbsp;<b>Map Layers</b>&nbsp;
               </Link>
               menu and drag layers up or down in the list.
            </Typography>}

            <hr style={{ marginTop: 14 }} />

            <label style={{ marginTop: 14, color: 'rgba(183, 28, 28, 0.87)' }}>Export Map Only</label>

            <TextField select style={{ width: '100%', marginTop: 14 }}
               label="Export Format"
               title="Export Format"
               slotProps={{
                  htmlInput: { id: "export-format" },
                  inputLabel: { htmlFor: "export-format" }
               }}
               value={exportFormatLabel}
               onChange={exportFormatChanged}
            >
               {Util_MUI.buildSelectMenuItems(exportFormats)}
            </TextField>

            <div className="app-flex-row-start" style={{ marginTop: 14 }}>
               <Button disabled={(dataTableVisible && includeDataTable) || includeLegend} variant="outlined" color="info" onClick={handleExport} title="Export Map" aria-label="Export Map">
                  <Icon color={printIconColor}>exit_to_app</Icon>
                  <span style={{ marginLeft: 7 }}></span>
                  Export
               </Button>

               {/*<Button style={{ marginTop: 7 }} disabled={(dataTableVisible && includeDataTable) || includeLegend} variant="outlined" color="info" onClick={handleGeoRef} title="Download georeferencing world file" aria-label="GeoRef">*/}
               {/*   GeoRef*/}
               {/*</Button>*/}
            </div>

            {((dataTableVisible && includeDataTable) || includeLegend) && <Typography variant="body2" style={{ margin: 7, marginTop: 14 }}>
               <b><i>Tip:</i></b>&nbsp;&nbsp;To enable exporting the map, exclude the data table and legend.
            </Typography>}

            {/*<Typography variant="body2" style={{ margin: 7, marginTop: 15 }}>*/}
            {/*   <b><i>Tip:</i></b>&nbsp;&nbsp;If you download a georeferencing world file, you will need to define a projection for it in your application before use.*/}
            {/*</Typography>*/}

         </div>
      </Paper>

   ); //end return.
} //end function.
